<template>
  <v-dialog
    v-model="overlay"
    dark
    transition="fade-transition"
    overlay-opacity="0.8"
    @click:outside="closeOverlay"
  >
    <MapViewerComponent
      :title="$t('device.list.item.tracking_record')"
      :deviceIdLabel="$t('device.name')"
      :defaultDateRangeValue="dateRange"
      :device="device"
      :mapState="mapState"
      :deviceRawDataJourney="devicePositions"
      :vtrObject="vtrObject"
      :layers="layers"
      :single="true"
      @close-map-viewer="closeOverlay"
      @handleDateRange="handleDateRange"
      @query-device-history="queryDeviceHistory"
    />
  </v-dialog>
</template>

<script setup lang="ts">
import useStore from '@/utils/store/common';
import { Moment } from 'moment';
import { computed, ref, watch } from 'vue';
import MapViewerComponent from '@/components/common/MapViewerComponent/MapViewerComponent.vue';

interface IGeometry {
  coordinates: number[][];
  type: string;
}

interface IGPSPosition {
  geometry: IGeometry;
}

const props = defineProps({
  device: {
    type: Object,
    default: () => ({})
  },
  overlay: {
    type: Boolean
  }
});

const emit = defineEmits(['close']);

const store = useStore();

const dateRange = ref([] as Moment[]);
const devicePositions = ref([] as number[][]);
const mapState = ref('empty');
const vtrObject = ref({});

const overlay = computed(() => props.overlay);

let dateRangeChanged = false;

const layers: string[] = ['raw'];

function closeOverlay() {
  emit('close', false);
  setTimeout(() => { mapState.value = 'empty'; }, 500);
}

function handleDateRange(newDateRange: Moment[]): void {
  dateRange.value = newDateRange;
  dateRangeChanged = true;
}

function queryDeviceHistory() {
  if (dateRange.value.length && dateRangeChanged) {
    const [
      start,
      end
    ] = dateRange.value.map((date, index): number => {
      const minimumDateScopeInSeconds = 59;

      if ((date.unix() === dateRange.value[0].unix()) && index) {
        return date.unix() + minimumDateScopeInSeconds;
      }
      return date.unix();
    });

    mapState.value = 'loading';
    store.dispatch('devices/queryDevicesLastPositions', {
      deviceId: `${props.device.shortDeviceId}`,
      start,
      end
    }).then((data) => {
      const { features } = data;
      vtrObject.value = data;
      const GPSPositions = features.map((element: IGPSPosition) => element.geometry.coordinates);

      mapState.value = GPSPositions.length > 0 ? 'fetched' : 'noResult';
      devicePositions.value = GPSPositions;
    }).catch(() => {
      mapState.value = 'error';
    });
  }
  dateRangeChanged = false;
}

watch(
  () => overlay,
  () => {
    if (overlay.value && dateRange.value.length) {
      dateRangeChanged = true;
      queryDeviceHistory();
    }
  },
  {
    immediate: true
  }
);
</script>
